import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Drawer,
  Typography,
  Divider,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Menu,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useCallback, useEffect, useMemo, useState} from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import {GridColDef, GridRowSelectionModel, GridRenderCellParams} from '@mui/x-data-grid';
import AddUserModal from 'src/components/Modals/AddTenant';
import {AccessGroup, Apartment, Tenant, TenantStatusEnum} from 'src/apis/autoGenerate/types';
import {
  tenantServiceDelete,
  tenantServiceFind,
  tenantServiceSendInvite,
  apartmentServiceFind,
  tenantServiceBulkImport,
  tenantServiceBatchUpdateCallMethod,
} from 'src/apis/autoGenerate/services';
import {tenantClient} from 'src/apis/tenantClient';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import message from 'src/components/Message';
import SearchInput from 'src/components/SearchInput';
import {LoadingButton} from '@mui/lab';
import {formatDate} from 'src/utils/common';
import {useOrgId, useCanEdit} from 'src/hooks';
import {usePdkAutoSyncStatus} from 'src/hooks/usePdkResidentLock';
import React from 'react';
import BatchImportDialog, {BatchImportResult} from 'src/components/BatchImport/BatchImport';
import {PageHeader, EntioDataGrid, StatusBadge, getTenantStatusVariant} from 'src/components/ui';
import {neutral, brand} from 'src/theme';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';
import {ReactComponent as SIPCallIcon} from 'src/assets/SIP_Call.svg';
import {ReactComponent as MobileAppIcon} from 'src/assets/Mobile_App.svg';

type Order = 'asc' | 'desc';
type OrderByField = 'firstName' | 'lastName' | 'createdAt';

// Define responsive drawer width
const drawerWidth = 240;
const collapsedDrawerWidth = 64;

export default function TenantPage() {
  const [addTenantModalOpen, setAddTenantModalOpen] = useState(false);
  const [batchInvite, setBatchInvite] = useState(false);
  const [batchDelete, setBatchDelete] = useState(false);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
  const [tenantDetailId, setTenantDetailId] = useState<string>('');
  const [tenantModalReadOnly, setTenantModalReadOnly] = useState(false);
  const [tenantModalUnitOnly, setTenantModalUnitOnly] = useState(false);
  // Currently used only for future filtering/search display
  const [searchApartment, setSearchApartment] = useState<Apartment | null>();
  const [searchTenant, setSearchTenant] = useState<string>('');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant>();
  const [tenantActionIdAnchorEl, setTenantActionIdAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const {orgId} = useOrgId();
  const account = useAppSelector(selectAccount);
  const canEdit = useCanEdit();
  const {locked: pdkResidentsLocked, licenseBlocked: pdkLicenseBlocked} = usePdkAutoSyncStatus();
  const [isDataReady, setIsDataReady] = useState(false);
  const tenantActionOpen = Boolean(tenantActionIdAnchorEl);
  const tenantActionId = tenantActionOpen ? 'tenant-action-popover' : undefined;
  const [order] = useState<Order>('asc');
  const [orderBy] = useState<OrderByField>('firstName');

  // New state variables for apartments
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loadingApartments, setLoadingApartments] = useState(false);
  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Add state for mobile menu toggle
  const [mobileOpen, setMobileOpen] = useState(false);
  // Add state for desktop sidebar collapse
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Add state for pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Add state for batch import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [batchImportLoading, setBatchImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<BatchImportResult | null>(null);
  const [batchCallMethodUpdate, setBatchCallMethodUpdate] = useState(false);
  const [batchActionsAnchorEl, setBatchActionsAnchorEl] = useState<null | HTMLElement>(null);
  const batchActionsOpen = Boolean(batchActionsAnchorEl);
  const [batchCallMethodValue, setBatchCallMethodValue] = useState<'MOBILE_APP' | 'SIP_PHONE'>(
    'MOBILE_APP'
  );

  const getCallMethodErrorMessage = (error: any): string => {
    const serverMessage =
      error?.response?.data?.error || error?.response?.data?.message || error?.message;

    if (typeof serverMessage === 'string') {
      if (serverMessage.includes('Insufficient SIP license quota')) {
        const availableMatch = serverMessage.match(/Available:\s*(-?\d+)/i);
        const neededMatch = serverMessage.match(/Needed:\s*(-?\d+)/i);
        const available = availableMatch ? availableMatch[1] : null;
        const needed = neededMatch ? neededMatch[1] : null;
        const suffix =
          available || needed ? ` (Available ${available ?? '?'}, Needed ${needed ?? '?'})` : '';
        return `SIP license quota is insufficient. Please add SIP licenses or free up SIP users.${suffix}`;
      }
      return serverMessage;
    }

    return 'Failed to update call method';
  };

  // Add license quota checking
  const {
    isAtLimit,
    isInsufficient,
    quota,
    activeLicensesCount,
    hasSipLicense,
    sipAvailableUnits,
    sipHasUnlimited,
    refetch: refetchLicenseQuota,
  } = useLicenseQuota();

  // Handle mobile drawer toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    if (orgId) {
      setIsDataReady(true);
      fetchApartments(); // Fetch apartments when orgId is available
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    // This useEffect will handle initial data loading
    if (orgId && !initialLoadComplete) {
      getList(); // Call without apartment ID to get all tenants
      setInitialLoadComplete(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, initialLoadComplete]);

  // Fetch all apartments for the left menu
  const fetchApartments = async () => {
    if (!orgId) return;

    setLoadingApartments(true);
    try {
      const userId = account.userId;
      const response = await apartmentServiceFind(`${orgId}`, undefined, undefined, userId);

      if (response && response.data && response.data.apartments) {
        if (response.data.apartments.length === 0) {
          setApartments([]);
          return;
        }

        const sortedApartments = response.data.apartments.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        });

        setApartments(sortedApartments);
      } else {
        console.warn('Invalid response structure:', response);
        setApartments([]);
      }
    } catch (error) {
      console.error('Error fetching apartments:', error);
      message.error(
        'Failed to load apartments: ' + (error instanceof Error ? error.message : 'Unknown error')
      );
      setApartments([]);
    } finally {
      setLoadingApartments(false);
    }
  };

  // Handle apartment selection from left menu
  const handleApartmentSelect = (apartmentId: string) => {
    setSelectedApartmentId(apartmentId);

    // Find the selected apartment to use for filtering
    const apartment = apartments.find(apt => apt.id === apartmentId);
    setSearchApartment(apartment || null);

    // Get tenants for this apartment
    getList(searchTenant, apartmentId);
  };

  // Clear apartment filter
  const clearApartmentFilter = () => {
    setSelectedApartmentId(null);
    setSearchApartment(null);
    // Fetch tenants from all accessible apartments, not all tenants in org
    const userAccessibleApartmentIds = apartments.map(apt => apt.id).filter(Boolean) as string[];
    getList(searchTenant, undefined, userAccessibleApartmentIds);
  };

  // Modified getList function to work with apartment filtering
  const getList = useCallback(
    async (name?: string, apartmentId?: string, apartmentIds?: string[]) => {
      if (!orgId) {
        console.error('orgId is not defined');
        return;
      }

      setLoading(true);
      try {
        let allTenants: Tenant[] = [];

        // If specific apartment ID is provided, fetch tenants for that apartment
        if (apartmentId) {
          const res = await tenantServiceFind(name, apartmentId, 1000, 1, `${orgId}`);
          if (res?.data?.tenants) {
            allTenants = res.data.tenants;
          }
        }
        // If multiple apartment IDs are provided (for "All Properties"), fetch from all accessible apartments
        else if (apartmentIds && apartmentIds.length > 0) {
          const tenantPromises = apartmentIds.map(aptId =>
            tenantServiceFind(name, aptId, 1000, 1, `${orgId}`)
          );

          const results = await Promise.allSettled(tenantPromises);

          results.forEach(result => {
            if (result.status === 'fulfilled' && result.value?.data?.tenants) {
              allTenants = [...allTenants, ...result.value.data.tenants];
            }
          });

          // Remove duplicate tenants based on tenant ID
          const uniqueTenants = allTenants.filter(
            (tenant, index, self) => index === self.findIndex(t => t.id === tenant.id)
          );
          allTenants = uniqueTenants;
        }
        // Fallback: if no filters provided, use all accessible apartments
        else {
          const userAccessibleApartmentIds = apartments
            .map(apt => apt.id)
            .filter(Boolean) as string[];
          if (userAccessibleApartmentIds.length > 0) {
            const tenantPromises = userAccessibleApartmentIds.map(aptId =>
              tenantServiceFind(name, aptId, 1000, 1, `${orgId}`)
            );

            const results = await Promise.allSettled(tenantPromises);

            results.forEach(result => {
              if (result.status === 'fulfilled' && result.value?.data?.tenants) {
                allTenants = [...allTenants, ...result.value.data.tenants];
              }
            });

            // Remove duplicate tenants based on tenant ID
            const uniqueTenants = allTenants.filter(
              (tenant, index, self) => index === self.findIndex(t => t.id === tenant.id)
            );
            allTenants = uniqueTenants;
          }
        }

        setTenants(allTenants.length ? allTenants : []);
      } catch (error) {
        console.error('Error fetching tenants:', error);
        message.error(
          'Failed to load tenants: ' + (error instanceof Error ? error.message : 'Unknown error')
        );
        setTenants([]);
      } finally {
        setLoading(false);
      }
    },
    [orgId, apartments]
  );

  const handleActionClose = () => {
    setTenantActionIdAnchorEl(null);
  };

  const handleSearchTenantByInfo = (tenant = '') => {
    setSearchTenant(tenant);
    getList(tenant, selectedApartmentId || undefined);
  };

  const handleTenantActionClick = (event: React.MouseEvent<HTMLButtonElement>, tenant: Tenant) => {
    setSelectedTenant(tenant);
    setTenantActionIdAnchorEl(event.currentTarget);
  };

  const handleTenantEdit = () => {
    setTenantActionIdAnchorEl(null);
    setTenantModalReadOnly(false);
    setTenantModalUnitOnly(false);
    setTenantDetailId('');
    setAddTenantModalOpen(true);
  };

  const handleTenantEditUnit = () => {
    setTenantActionIdAnchorEl(null);
    setTenantModalReadOnly(false);
    setTenantModalUnitOnly(true);
    setTenantDetailId('');
    setAddTenantModalOpen(true);
  };

  const handleTenantView = () => {
    setTenantActionIdAnchorEl(null);
    setTenantModalReadOnly(true);
    setTenantModalUnitOnly(false);
    setTenantDetailId(selectedTenant?.id || '');
    setAddTenantModalOpen(true);
  };

  const handleSendEmail = async () => {
    try {
      setSendEmailLoading(true);
      setTenantActionIdAnchorEl(null);

      if (!orgId) {
        throw new Error('Organization ID is missing');
      }

      await tenantServiceSendInvite({
        ids:
          selectedRowIds.length > 0 ? (selectedRowIds as string[]) : [selectedTenant?.id as string],
        orgId,
      });

      message.success('Invite Success');
    } catch (error) {
      console.error('Invite error:', error);
      message.error(error instanceof Error ? error.message : 'Invite Failed');
    } finally {
      setSendEmailLoading(false);
    }
  };

  const handleRemoveTenant = () => {
    setLoading(true);
    tenantServiceDelete(selectedTenant?.id as string).then(() => {
      message.success('Remove Success');
      getList(searchTenant, selectedApartmentId || undefined);
    });
  };

  const handleBatchDeleteResidents = async () => {
    const ids = (selectedRowIds as string[]).filter(Boolean);
    if (ids.length === 0) return;
    setBatchDeleting(true);
    try {
      const res = await tenantClient.batchDelete(ids);
      const result = res.data;
      setSelectedRowIds([]);
      setBatchDeleteOpen(false);
      setBatchDelete(false);
      getList(searchTenant, selectedApartmentId || undefined);
      refetchLicenseQuota();
      if (result.failedCount === 0) {
        message.success(
          `Deleted ${result.deletedCount} resident${result.deletedCount === 1 ? '' : 's'}`
        );
      } else if (result.deletedCount === 0) {
        message.error(
          result.failed[0]?.message || `Failed to delete ${result.failedCount} resident(s)`
        );
      } else {
        message.warning(
          `Deleted ${result.deletedCount}, failed ${result.failedCount}. Check console for details.`
        );
        console.error('Batch delete failures:', result.failed);
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || err?.message || 'Batch delete failed');
    } finally {
      setBatchDeleting(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'firstName',
        headerName: 'First Name',
        width: 100,
        flex: 0.7,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        width: 100,
        flex: 0.7,
      },
      {
        field: 'room',
        headerName: 'Unit Name',
        width: 80,
        flex: 0.5,
        renderCell: val => {
          return <div>{val.value.name}</div>;
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 180,
        flex: 1,
        renderCell: params => {
          return (
            <Box
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}
              title={params.value}
            >
              {params.value}
            </Box>
          );
        },
      },
      {
        field: 'accessGroups',
        headerName: 'Access Group',
        width: 130,
        flex: 0.8,
        renderCell: val => {
          const accessGroups = val.value as AccessGroup[];
          return accessGroups ? (
            <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
              {accessGroups.map(_ => (
                <Box
                  sx={{
                    bgcolor: neutral[200],
                    margin: '0 5px 5px 0',
                    borderRadius: '5px',
                    padding: '3px',
                    fontSize: '0.75rem',
                  }}
                  key={_.id}
                >
                  {_.name}
                </Box>
              ))}
            </Box>
          ) : (
            <Box>--</Box>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 90,
        flex: 0.6,
        renderCell: params => {
          const status = params.value;
          let label = status || 'Unknown';
          if (status === TenantStatusEnum.Enabled || status === 'active') label = 'Activated';
          else if (status === TenantStatusEnum.Pending || status === 'invited') label = 'Invited';
          else if (status === TenantStatusEnum.Created || status === 'created') label = 'Created';
          return <StatusBadge status={getTenantStatusVariant(status)} label={label} />;
        },
      },
      ...(hasSipLicense
        ? [
            {
              field: 'callMethod',
              headerName: 'Call Method',
              width: 130,
              flex: 0.8,
              renderCell: (params: GridRenderCellParams<Tenant>) => {
                const tenant = params.row as Tenant;
                const callMethod = tenant.user?.callMethod || 'MOBILE_APP';

                const label = callMethod === 'SIP_PHONE' ? 'SIP Phone' : 'Mobile App';
                const Icon = callMethod === 'SIP_PHONE' ? SIPCallIcon : MobileAppIcon;

                return (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Icon style={{width: 16, height: 16}} />
                    <Typography variant="body2" sx={{fontSize: '0.8rem'}}>
                      {label}
                    </Typography>
                  </Box>
                );
              },
            },
          ]
        : []),
      {
        field: 'createdAt',
        headerName: 'Create Date',
        width: 110,
        flex: 0.7,
        renderCell: val => {
          return <div>{formatDate(val.value)}</div>;
        },
      },
      ...(canEdit
        ? [
            {
              field: 'id',
              headerName: '',
              width: 40,
              flex: 0,
              renderCell: (val: any) => {
                return (
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      handleTenantActionClick(e, val.row);
                    }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                );
              },
            },
          ]
        : []),
    ],
    [canEdit, apartments, selectedApartmentId, searchTenant, getList, hasSipLicense]
  );

  const sortedTenants = useMemo(() => {
    const compareValues = (a: Tenant, b: Tenant, orderByField: OrderByField): number => {
      const valA = a[orderByField as keyof Tenant];
      const valB = b[orderByField as keyof Tenant];

      if (valA == null || valB == null) return 0;

      if (valB < valA) {
        return order === 'asc' ? 1 : -1;
      }
      if (valB > valA) {
        return order === 'asc' ? -1 : 1;
      }
      return 0;
    };

    return [...tenants].sort((a: Tenant, b: Tenant) => {
      if (orderBy === 'createdAt') {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;

        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }

      return compareValues(a, b, orderBy);
    });
  }, [tenants, order, orderBy]);

  // Get the current apartment name for display
  const currentApartmentName = useMemo(() => {
    if (!selectedApartmentId) return 'All Properties';
    const apartment = apartments.find(apt => apt.id === selectedApartmentId);
    return apartment?.name ?? 'Selected Property';
  }, [selectedApartmentId, apartments]);

  // Drawer content - extracted as a component for reuse
  const drawerContent = (
    <>
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${neutral[200]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
        }}
      >
        {!sidebarCollapsed && (
          <Typography variant="h6" fontWeight="medium">
            Properties
          </Typography>
        )}
        {!isMobile && (
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            sx={{
              ml: sidebarCollapsed ? 0 : 'auto',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>

      <List sx={{overflow: 'auto'}}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={clearApartmentFilter}
            selected={!selectedApartmentId}
            sx={{
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              '&.Mui-selected': {
                backgroundColor: brand[50],
                '&:hover': {
                  backgroundColor: brand[50],
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  backgroundColor: theme.palette.primary.main,
                },
              },
            }}
            title={sidebarCollapsed ? 'All Properties' : ''}
          >
            <ListItemIcon sx={{minWidth: sidebarCollapsed ? 0 : 40, justifyContent: 'center'}}>
              <ApartmentIcon fontSize="small" />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText
                primary="All Properties"
                primaryTypographyProps={{fontSize: '0.95rem'}}
              />
            )}
          </ListItemButton>
        </ListItem>

        {!sidebarCollapsed && <Divider sx={{my: 1, opacity: 0.6}} />}

        {loadingApartments ? (
          <ListItem>
            <ListItemText
              primary="Loading properties..."
              primaryTypographyProps={{fontSize: '0.9rem', color: 'text.secondary'}}
            />
          </ListItem>
        ) : apartments.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No properties found"
              primaryTypographyProps={{fontSize: '0.9rem', color: 'text.secondary'}}
            />
          </ListItem>
        ) : (
          apartments.map(apartment => (
            <ListItem key={apartment.id} disablePadding>
              <ListItemButton
                onClick={() => handleApartmentSelect(apartment.id as string)}
                selected={selectedApartmentId === apartment.id}
                sx={{
                  py: 1,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': {
                    backgroundColor: brand[50],
                    '&:hover': {
                      backgroundColor: brand[50],
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '3px',
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                }}
                title={sidebarCollapsed ? apartment.name : ''}
              >
                <ListItemIcon sx={{minWidth: sidebarCollapsed ? 0 : 40, justifyContent: 'center'}}>
                  {selectedApartmentId === apartment.id ? (
                    <ArrowRightIcon fontSize="small" color="primary" />
                  ) : (
                    <ApartmentIcon fontSize="small" sx={{opacity: 0.7}} />
                  )}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={apartment.name}
                    primaryTypographyProps={{fontSize: '0.95rem'}}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        marginLeft: -5, // Remove left padding gap from Layout
        marginRight: -5, // Remove right padding gap from Layout
        marginTop: -5, // Remove top padding gap from Layout
        marginBottom: -5, // Remove bottom padding gap from Layout
        width: 'calc(100% + 80px)', // Extend to fill parent padding (5 * 8px * 2 = 80px)
        height: 'calc(100% + 80px)', // Extend to fill parent padding (5 * 8px * 2 = 80px)
      }}
    >
      {/* Mobile drawer toggle button - only visible on small screens */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1200,
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar - ensure it's always visible */}
      <Box
        component="nav"
        sx={{
          width: {
            md: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
          },
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: 1000,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* For mobile drawer */}
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: {xs: 'block', md: 'none'},
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                borderRight: 'none',
                boxShadow: '0 0 10px rgba(0,0,0,0.1)',
              },
            }}
          >
            {drawerContent}
          </Drawer>
        ) : (
          /* Desktop drawer - collapsible */
          <Drawer
            variant="permanent"
            sx={{
              display: {xs: 'none', md: 'block'},
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              flexShrink: 0,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              '& .MuiDrawer-paper': {
                position: 'static',
                width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
                height: '100%',
                borderRight: `1px solid ${neutral[200]}`,
                boxShadow: 'none',
                background: neutral[25],
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflowX: 'hidden',
              },
            }}
            open
          >
            {drawerContent}
          </Drawer>
        )}
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flexGrow: 1,
          p: {xs: 2, md: 3},
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Content elements */}
        <PageHeader title={currentApartmentName} />
        {pdkResidentsLocked && (
          <Alert severity="info" sx={{mb: 2}}>
            Residents are managed from PDK while auto-sync is on. Add or delete
            people and edit name/email in pdk.io — Entrio will sync automatically.
            You can still change unit assignment here.
            {(pdkLicenseBlocked || isInsufficient) &&
              ' New PDK people will not be created here until you add unit licenses.'}
          </Alert>
        )}

        {/* Existing toolbar with search and action buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column', sm: 'row'},
            justifyContent: 'space-between',
            alignItems: {xs: 'flex-start', sm: 'center'},
            mb: 2,
            gap: 2,
          }}
        >
          <Stack
            spacing={2}
            direction={{xs: 'column', sm: 'row'}}
            alignItems={{xs: 'flex-start', sm: 'center'}}
            width={{xs: '100%', sm: 'auto'}}
          >
            <SearchInput
              placeholder="Search Resident"
              onSearch={handleSearchTenantByInfo}
              sx={{width: {xs: '100%', sm: 'auto'}}}
            />
          </Stack>
          {canEdit && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outlined"
                onClick={event => setBatchActionsAnchorEl(event.currentTarget)}
                startIcon={<MoreVertIcon />}
                sx={{
                  borderRadius: 16,
                  height: 40,
                  width: {xs: '100%', sm: 'auto'},
                }}
              >
                Batch Actions
              </Button>
              <Menu
                anchorEl={batchActionsAnchorEl}
                open={batchActionsOpen}
                onClose={() => setBatchActionsAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <MenuItem
                  onClick={() => {
                    setBatchInvite(!batchInvite);
                    setBatchCallMethodUpdate(false);
                    setBatchDelete(false);
                    setBatchActionsAnchorEl(null);
                  }}
                  selected={batchInvite}
                >
                  <ListItemIcon>
                    <EmailIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Batch Invite" />
                  {batchInvite && <ClearIcon fontSize="small" sx={{ml: 1}} />}
                </MenuItem>
                {hasSipLicense && (
                  <MenuItem
                    onClick={() => {
                      setBatchCallMethodUpdate(!batchCallMethodUpdate);
                      setBatchInvite(false);
                      setBatchDelete(false);
                      setBatchActionsAnchorEl(null);
                    }}
                    selected={batchCallMethodUpdate}
                  >
                    <ListItemIcon>
                      <PhoneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Batch Update Call Method" />
                    {batchCallMethodUpdate && <ClearIcon fontSize="small" sx={{ml: 1}} />}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setBatchDelete(!batchDelete);
                    setBatchInvite(false);
                    setBatchCallMethodUpdate(false);
                    setBatchActionsAnchorEl(null);
                  }}
                  selected={batchDelete}
                  disabled={pdkResidentsLocked}
                >
                  <ListItemIcon>
                    <DeleteOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Batch Delete" />
                  {batchDelete && <ClearIcon fontSize="small" sx={{ml: 1}} />}
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setImportResult(null);
                    setImportOpen(true);
                    setBatchActionsAnchorEl(null);
                  }}
                  disabled={
                    pdkResidentsLocked || isAtLimit || isInsufficient || !quota || activeLicensesCount === 0
                  }
                >
                  <ListItemIcon>
                    <UploadFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Batch Import" />
                </MenuItem>
              </Menu>
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedTenant(undefined);
                  setTenantModalReadOnly(false);
                  setTenantModalUnitOnly(false);
                  setTenantDetailId('');
                  setAddTenantModalOpen(true);
                }}
                disabled={pdkResidentsLocked}
                startIcon={<AddIcon />}
                sx={{
                  width: {xs: '100%', sm: 'auto'},
                }}
              >
                Resident
              </Button>
            </Box>
          )}
        </Box>

        {/* Batch invite bar */}
        {batchInvite && (
          <Box
            sx={{
              bgcolor: 'rgba(233, 240, 255, 0.6)',
              p: '8px 16px',
              mt: 2,
              mb: 2,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              position: 'relative',
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
              <Typography variant="body2">{selectedRowIds.length} user(s) selected</Typography>
              <LoadingButton
                loading={sendEmailLoading}
                loadingPosition="start"
                variant="contained"
                disabled={selectedRowIds.length < 1}
                onClick={handleSendEmail}
                startIcon={<AddIcon />}
                size="small"
                sx={{borderRadius: 16}}
              >
                Invite
              </LoadingButton>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setBatchInvite(false);
                setSelectedRowIds([]);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Batch call method update bar */}
        {batchCallMethodUpdate && (
          <Box
            sx={{
              bgcolor: 'rgba(233, 240, 255, 0.6)',
              p: '8px 16px',
              mt: 2,
              mb: 2,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              position: 'relative',
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
              <Typography variant="body2">{selectedRowIds.length} user(s) selected</Typography>
              {batchCallMethodValue === 'SIP_PHONE' && (
                <Typography variant="body2" sx={{color: 'text.secondary'}}>
                  {sipHasUnlimited
                    ? 'Unlimited SIP licenses'
                    : `${sipAvailableUnits} SIP license(s) available`}
                </Typography>
              )}
              <FormControl size="small" sx={{minWidth: 150}}>
                <Select
                  value={batchCallMethodValue}
                  onChange={e =>
                    setBatchCallMethodValue(e.target.value as 'MOBILE_APP' | 'SIP_PHONE')
                  }
                  sx={{height: '32px'}}
                >
                  <MenuItem value="MOBILE_APP">
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <MobileAppIcon style={{width: 16, height: 16}} />
                      <span>Mobile App</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="SIP_PHONE">
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <SIPCallIcon style={{width: 16, height: 16}} />
                      <span>SIP Phone</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              <LoadingButton
                loading={sendEmailLoading}
                loadingPosition="start"
                variant="contained"
                disabled={selectedRowIds.length < 1}
                onClick={async () => {
                  if (selectedRowIds.length === 0) return;

                  // Only residents not already on SIP consume a new seat
                  if (batchCallMethodValue === 'SIP_PHONE' && !sipHasUnlimited) {
                    const seatsNeeded = selectedRowIds.filter(id => {
                      const tenant = tenants.find(t => t.id === id);
                      return tenant?.user?.callMethod !== 'SIP_PHONE';
                    }).length;

                    if (seatsNeeded > sipAvailableUnits) {
                      message.error(
                        `Not enough SIP licenses. This change needs ${seatsNeeded} more SIP seat(s) but only ${sipAvailableUnits} are available.`
                      );
                      return;
                    }
                  }

                  setSendEmailLoading(true);
                  try {
                    const response = await tenantServiceBatchUpdateCallMethod({
                      tenantIds: selectedRowIds as string[],
                      callMethod: batchCallMethodValue,
                    });
                    if (response.data.success) {
                      message.success(
                        `Successfully updated ${response.data.updated} tenant(s). ${
                          response.data.failed > 0 ? `${response.data.failed} failed.` : ''
                        }`
                      );
                      if (response.data.errors && response.data.errors.length > 0) {
                        console.error('Update errors:', response.data.errors);
                      }
                      getList(searchTenant, selectedApartmentId || undefined);
                      refetchLicenseQuota();
                      setSelectedRowIds([]);
                      setBatchCallMethodUpdate(false);
                    } else {
                      message.error('Failed to update call method');
                    }
                  } catch (error) {
                    message.error(getCallMethodErrorMessage(error));
                  } finally {
                    setSendEmailLoading(false);
                  }
                }}
                size="small"
                sx={{borderRadius: 16}}
              >
                Update
              </LoadingButton>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setBatchCallMethodUpdate(false);
                setSelectedRowIds([]);
                setBatchCallMethodValue('MOBILE_APP'); // Reset to default
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Batch delete bar */}
        {batchDelete && (
          <Box
            sx={{
              bgcolor: 'rgba(255, 235, 238, 0.7)',
              p: '8px 16px',
              mt: 2,
              mb: 2,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              position: 'relative',
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
              <Typography variant="body2">{selectedRowIds.length} resident(s) selected</Typography>
              <LoadingButton
                loading={batchDeleting}
                loadingPosition="start"
                variant="contained"
                color="error"
                disabled={selectedRowIds.length < 1 || batchDeleting}
                onClick={() => setBatchDeleteOpen(true)}
                startIcon={<DeleteOutlineIcon />}
                size="small"
                sx={{borderRadius: 16}}
              >
                Delete
              </LoadingButton>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setBatchDelete(false);
                setSelectedRowIds([]);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* For the data grid container */}
        <Box
          sx={{
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            borderTop: `1px solid ${neutral[200]}`,
            borderBottom: `1px solid ${neutral[200]}`,
            backgroundColor: 'white',
            mt: 2,
            mb: 3,
            flexGrow: 1,
            minHeight: 0,
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 5,
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
              }}
            >
              <Typography
                variant="body2"
                color="primary"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.8rem',
                }}
              >
                Loading residents...
              </Typography>
            </Box>
          )}

          <EntioDataGrid
            loading={loading}
            rows={sortedTenants}
            columns={columns}
            emptyMessage={
              selectedApartmentId ? 'No residents in this property' : 'No residents found'
            }
            initialState={{
              pagination: {
                paginationModel: {page: 0, pageSize: 25},
              },
              sorting: {
                sortModel: [{field: 'createdAt', sort: 'desc'}],
              },
            }}
            onRowSelectionModelChange={res => {
              setSelectedRowIds(res);
            }}
            pageSizeOptions={[10, 25, 50]}
            checkboxSelection={batchInvite || batchCallMethodUpdate || batchDelete}
            disableRowSelectionOnClick={true}
            pagination
            paginationMode="client"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sx={{
              border: 'none',
              minHeight: 400,
              width: '100%',
              flex: 1,
              '& .MuiDataGrid-root': {
                width: '100%',
              },
              '& .MuiDataGrid-main': {
                overflowX: 'auto',
                overflowY: 'auto',
                width: '100%',
              },
              '& .MuiDataGrid-virtualScroller': {
                backgroundColor: 'transparent',
                overflowX: 'auto !important',
                overflowY: 'auto !important',
              },
              '& .MuiDataGrid-columnHeaders': {
                minWidth: 'max-content',
              },
              '& .MuiDataGrid-row': {
                minWidth: 'max-content',
                '&:focus': {
                  outline: 'none !important',
                },
                '&:focus-within': {
                  outline: 'none !important',
                },
              },
              // Remove all focus outlines and selection boxes
              '& .MuiDataGrid-cell': {
                outline: 'none !important',
                '&:focus': {
                  outline: 'none !important',
                },
                '&:focus-within': {
                  outline: 'none !important',
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Existing modals and popovers */}
      {addTenantModalOpen && isDataReady && (
        <AddUserModal
          tenantDetailId={tenantDetailId}
          readOnly={tenantModalReadOnly}
          unitOnly={tenantModalUnitOnly}
          open={addTenantModalOpen}
          initData={selectedTenant}
          handleCancel={() => {
            setAddTenantModalOpen(false);
            setTenantModalReadOnly(false);
            setTenantModalUnitOnly(false);
            setTenantDetailId('');
          }}
          handleConfirm={() => {
            setAddTenantModalOpen(false);
            setTenantModalReadOnly(false);
            setTenantModalUnitOnly(false);
            setTenantDetailId('');
            getList(searchTenant, selectedApartmentId || undefined);
            refetchLicenseQuota();
          }}
        />
      )}

      <Dialog
        open={batchDeleteOpen}
        onClose={batchDeleting ? undefined : () => setBatchDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete selected residents?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to delete <strong>{selectedRowIds.length}</strong> resident
            {selectedRowIds.length === 1 ? '' : 's'}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteOpen(false)} disabled={batchDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleBatchDeleteResidents()}
            disabled={batchDeleting}
          >
            {batchDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        id={tenantActionId}
        open={tenantActionOpen}
        anchorEl={tenantActionIdAnchorEl}
        onClose={handleActionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          {canEdit && (
            <>
              {pdkResidentsLocked ? (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={handleTenantView}>
                      <ListItemText primary="View Resident" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton onClick={handleTenantEditUnit}>
                      <ListItemText primary="Edit Unit" />
                    </ListItemButton>
                  </ListItem>
                </>
              ) : (
                <ListItem disablePadding>
                  <ListItemButton onClick={handleTenantEdit}>
                    <ListItemText primary="Edit Resident" />
                  </ListItemButton>
                </ListItem>
              )}
              {selectedTenant?.status !== TenantStatusEnum.Enabled && (
                <ListItem disablePadding>
                  <ListItemButton onClick={handleSendEmail}>
                    <ListItemText primary="Resend Invite" />
                  </ListItemButton>
                </ListItem>
              )}
              {!pdkResidentsLocked && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setTenantActionIdAnchorEl(null);
                      handleRemoveTenant();
                    }}
                  >
                    <ListItemText primary="Remove" />
                  </ListItemButton>
                </ListItem>
              )}
            </>
          )}
        </List>
      </Popover>

      <BatchImportDialog
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportResult(null);
        }}
        result={importResult}
        onFileSelected={async file => {
          if (!orgId) {
            message.error('Organization ID is missing');
            return;
          }
          setBatchImportLoading(true);
          setImportResult(null);
          try {
            const formData = new FormData();
            formData.append('csvFile', file);
            formData.append('orgId', orgId);

            const response = await tenantServiceBulkImport(formData);
            const result = response.data;
            setImportResult(result);

            const importedCount = result.results?.success?.length || 0;
            const skippedCount = result.results?.errors?.length || 0;

            if (importedCount > 0) {
              getList(searchTenant, selectedApartmentId || undefined);
            }

            if (importedCount > 0 && skippedCount === 0) {
              message.success(result.message || 'Batch import completed!');
            } else if (importedCount > 0) {
              message.warning(result.message || 'Batch import completed with skipped rows.');
            } else {
              message.error(result.message || 'Batch import failed!');
            }
          } catch (err: any) {
            message.error(err?.message || 'Batch import failed!');
          } finally {
            setBatchImportLoading(false);
          }
        }}
        loading={batchImportLoading}
      />
    </Box>
  );
}
